/**
 * Command palette registry provider (Slice E4).
 *
 * Maintains the set of contextual command sections contributed by mounted
 * pages. Outside this provider the registry hooks fall back to a no-op so
 * the palette can mount globally without forcing every layout to wrap one.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  CommandPaletteRegistryCtx,
  type CommandPaletteRegistry,
  type CommandPaletteSection,
} from './command-palette-registry-context';

export function CommandPaletteRegistryProvider({ children }: { readonly children: ReactNode }) {
  const [sectionsById, setSectionsById] = useState<Map<string, CommandPaletteSection>>(
    () => new Map(),
  );

  const registerSection = useCallback((section: CommandPaletteSection) => {
    setSectionsById((current) => {
      const next = new Map(current);
      next.set(section.id, section);
      return next;
    });
    return () => {
      setSectionsById((current) => {
        if (!current.has(section.id)) return current;
        const next = new Map(current);
        next.delete(section.id);
        return next;
      });
    };
  }, []);

  const sections = useMemo(
    () =>
      Array.from(sectionsById.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [sectionsById],
  );

  const value = useMemo<CommandPaletteRegistry>(
    () => ({ sections, registerSection }),
    [sections, registerSection],
  );

  return (
    <CommandPaletteRegistryCtx.Provider value={value}>
      {children}
    </CommandPaletteRegistryCtx.Provider>
  );
}
